import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { RootState } from '../store'
import { logoutRequest } from '../store/actions/authActions'
import {
  HomeIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  Bars3Icon,
  XMarkIcon,
  BriefcaseIcon,
  QueueListIcon,
  ShieldCheckIcon,
  WindowIcon,
  BeakerIcon,
  PresentationChartBarIcon,
  ClipboardDocumentCheckIcon,
  CommandLineIcon,
  CalculatorIcon,
  ChartBarIcon,
  LinkIcon,
  LightBulbIcon,
  LockClosedIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import { Crown, LayoutDashboard, Settings, UserCircle } from 'lucide-react'
import { SubscriptionModal } from './ui/SubscriptionModal'
import toast from 'react-hot-toast'

interface LayoutProps {
  children: React.ReactNode
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation()
  const dispatch = useDispatch()
  const { user } = useSelector((state: RootState) => state.auth)
  const { projects, activeProject: reduxActiveProject } = useSelector((state: RootState) => state.projects)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [limitMessage, setLimitMessage] = useState('')
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Detect active project strictly from URL to prevent stale state on navigation
  const projectId = useMemo(() => {
    const match = location.pathname.match(/\/projects\/([^\/]+)/)
    const id = match ? match[1] : null
    return id === 'create' ? null : id
  }, [location.pathname])

  const activeProject = useMemo(() => {
    if (reduxActiveProject?.id === projectId) return reduxActiveProject
    if (!projectId) return null
    return projects.find(p => p.id === projectId)
  }, [projectId, projects, reduxActiveProject])

  useEffect(() => {
    const handleLimitReached = (event: CustomEvent) => {
      setLimitMessage(event.detail?.message || 'Plan limit reached. Upgrade to unlock.')
      setShowSubscriptionModal(true)
    }

    const handleDailyLimitReached = (event: CustomEvent) => {
      const msg = event.detail?.message || 'Today limit is over'
      setLimitMessage(msg)
      setShowSubscriptionModal(true)
      toast.error(msg, {
        duration: 5000,
        position: 'top-center',
        style: {
          background: '#FFF7ED',
          color: '#C2410C',
          border: '1px solid #FED7AA',
          fontWeight: 'bold'
        }
      })
    }

    window.addEventListener('plan-limit-reached' as any, handleLimitReached)
    window.addEventListener('daily-limit-reached' as any, handleDailyLimitReached)

    return () => {
      window.removeEventListener('plan-limit-reached' as any, handleLimitReached)
      window.removeEventListener('daily-limit-reached' as any, handleDailyLimitReached)
    }
  }, [])

  const navigation = user?.role === 'admin'
    ? []
    : [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
      { name: 'AI Strategy', href: '/ai-strategy', icon: LightBulbIcon },
      { name: 'Drafts', href: '/drafts', icon: DocumentTextIcon },
    ]

  const projectNavigation = useMemo(() => {
    if (!activeProject) return []
    
    const reqs = activeProject.requirements || []
    const getCount = (type: string) => {
      if (type === 'BP') {
        return (activeProject as any).businessProcess?.mermaidCode ? 1 : 0
      }
      return reqs.filter((r: any) => r.type === type).length
    }
    const isVerified = (type: string) => {
      if (type === 'BP') {
        return !!(activeProject as any).businessProcess?.mermaidCode
      }
      const typeReqs = reqs.filter((r: any) => r.type === type)
      return typeReqs.length > 0 && typeReqs.every((r: any) => r.isVerified)
    }

    // Prerequisites: BRD, PRD, NFR, and UIR must all be completed and verified
    const isNonTech = activeProject?.projectType === 'NON_TECH'

    const prerequisitesMet = isNonTech ? true : (
      isVerified('BRD') &&
      isVerified('PRD') &&
      isVerified('NFR') &&
      isVerified('UIR')
    )

    const PREREQ_TOOLTIP = 'Business Requirements Document (BRD)\nProduct Requirements Document (PRD)\nNon-Functional Requirements (NFR)\nUI Requirements (UIR)\nshould be verified'

    const allItems = [
      { name: 'Overview', tab: 'project', icon: BriefcaseIcon },
      { name: 'Business Requirements', tab: 'brd', icon: QueueListIcon, count: getCount('BRD'), verified: isVerified('BRD'), color: 'blue', isTechOnly: true },
      { name: 'Product Requirements', tab: 'prd', icon: QueueListIcon, count: getCount('PRD'), verified: isVerified('PRD'), color: 'purple', isTechOnly: true },
      { name: 'Non-Functional', tab: 'nfr', icon: ShieldCheckIcon, count: getCount('NFR'), verified: isVerified('NFR'), color: 'emerald', isTechOnly: true },
      { name: 'UI Requirements', tab: 'uir', icon: WindowIcon, count: getCount('UIR'), verified: isVerified('UIR'), color: 'orange', isTechOnly: true },
      { name: 'Business Process', tab: 'bp', icon: CommandLineIcon, count: getCount('BP'), verified: isVerified('BP'), color: 'pink', isLocked: !prerequisitesMet, lockTooltip: PREREQ_TOOLTIP, isTechOnly: true },
      { name: 'Market Intelligence', tab: 'product-intelligence', icon: PresentationChartBarIcon, color: 'indigo', isLocked: !prerequisitesMet, lockTooltip: PREREQ_TOOLTIP },
      { name: 'Test Cases', tab: 'test-cases', icon: BeakerIcon, isTechOnly: true },
      { name: 'Estimation Calculator', tab: 'estimation', icon: CalculatorIcon, isLocked: !prerequisitesMet, lockTooltip: PREREQ_TOOLTIP, isTechOnly: true },
      { name: 'Integrations', tab: 'integrations', icon: LinkIcon, isBeta: true, isDisabled: import.meta.env.VITE_ENABLE_INTEGRATIONS !== 'true' },
      { name: 'Strategic Initiatives', tab: 'strategic-initiatives', icon: ChartBarIcon, isBeta: true, isDisabled: import.meta.env.VITE_ENABLE_STRATEGIC_INITIATIVES !== 'true' },
    ]

    return isNonTech ? allItems.filter(item => !item.isTechOnly) : allItems
  }, [activeProject])

  const handleLogout = () => {
    dispatch(logoutRequest())
  }

  const toggleSidebar = () => setIsCollapsed(!isCollapsed)

  const activeTab = new URLSearchParams(location.search).get('tab') || 'project'

  return (
    <div className="flex min-h-screen bg-app-bg text-text font-sans selection:bg-primary/20 selection:text-white transition-colors duration-300 relative overflow-hidden">
      {/* Background Glows for "Agentic" Midnight theme */}
      <div className="fixed top-[-10%] left-[-10%] w-[40%] h-[40%] bg-info/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="fixed bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />

      {/* Sidebar - Desktop */}
      <aside 
        className={`fixed inset-y-0 left-0 z-40 glass border-r border-white/5 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] hidden lg:flex flex-col shadow-[4px_0_24px_rgba(0,0,0,0.5)] ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Sidebar Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 overflow-hidden px-1 h-20 items-center justify-center">
            <img 
              src="/DocSprintLogo.png" 
              alt="DocSprintAI" 
              className={`transition-all duration-300 ${isCollapsed ? 'h-12 w-auto' : 'h-16 w-auto'}`}
              style={{ filter: "drop-shadow(0px 0px 8px rgba(255,107,0,0.5))" }}
            />
          </Link>

        {/* Sidebar Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-1 py-4 custom-scrollbar">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href && !projectId
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 group relative ${
                  isActive 
                    ? 'bg-primary/20 text-primary shadow-[0_0_15px_rgba(255,107,0,0.15)] border border-primary/30' 
                    : 'text-text-secondary hover:bg-white/5 hover:text-white border border-transparent'
                }`}
              >
                <item.icon className={`h-4 w-4 transition-transform duration-200 ${isActive ? 'text-primary scale-110' : 'text-text-secondary group-hover:text-white group-hover:scale-110'}`} />
                {!isCollapsed && <span className="whitespace-nowrap animate-in fade-in slide-in-from-left-1">{item.name}</span>}
                {isCollapsed && (
                  <div className="absolute left-14 bg-card border border-white/10 text-white shadow-xl text-xs px-2 py-1 rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap pointer-events-none z-50">
                    {item.name}
                  </div>
                )}
              </Link>
            )
          })}

          {/* Project Workspace Section */}
          {projectId && (
            <div className={`mt-6 space-y-1 animate-in fade-in duration-500 ${isCollapsed ? 'items-center' : ''}`}>
              {!isCollapsed && (
                <div className="px-3 mb-3 flex items-center gap-2">
                  <div className="h-px bg-white/10 flex-1" />
                  <div className="flex items-center gap-1 group">
                     <span className="w-1.5 h-1.5 rounded-full bg-info animate-pulse-subtle shadow-[0_0_8px_theme(colors.info)]" />
                     <span className="text-[9px] font-bold text-info uppercase tracking-[0.2em] whitespace-nowrap opacity-80 group-hover:opacity-100 transition-opacity">AGENT ACTIVE</span>
                  </div>
                  <div className="h-px bg-white/10 flex-1" />
                </div>
              )}
              {projectNavigation.map((item) => {
                const isActive = activeTab === item.tab && location.pathname.includes(`/projects/${projectId}`)
                const isItemLocked = (item as any).isLocked
                return (
                  <Link
                    key={item.name}
                    to={item.isDisabled || isItemLocked ? '#' : `/projects/${projectId}?tab=${item.tab}`}
                    onClick={(e) => {
                      if (item.isDisabled) {
                        e.preventDefault();
                        toast.error(`${item.name} is coming soon!`, {
                          id: `disabled-${item.tab}`,
                          duration: 2000
                        });
                      } else if (isItemLocked) {
                        e.preventDefault();
                        toast.error((item as any).lockTooltip || 'Complete prerequisites first', {
                          id: `locked-${item.tab}`,
                          duration: 3000,
                          icon: '🔒'
                        });
                      }
                    }}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 group relative ${
                      isActive 
                        ? 'bg-primary/20 text-primary shadow-[0_0_15px_rgba(255,107,0,0.15)] border border-primary/30' 
                        : item.isDisabled || isItemLocked
                          ? 'opacity-40 cursor-not-allowed text-text-secondary border border-transparent'
                          : 'text-text-secondary hover:bg-white/5 hover:text-white border border-transparent'
                    }`}
                    title={isItemLocked ? (item as any).lockTooltip : undefined}
                  >
                    <item.icon className={`h-4 w-4 transition-transform duration-200 ${isActive ? 'text-primary scale-110' : 'text-text-secondary group-hover:text-white group-hover:scale-110'}`} />
                    {!isCollapsed && (
                      <div className="flex-1 flex items-center justify-between min-w-0">
                        <span className="whitespace-nowrap animate-in fade-in slide-in-from-left-1 text-[13px] truncate">{item.name}</span>
                        <div className="flex items-center gap-1.5 ml-1">
                           {item.verified && (
                             <ShieldCheckIcon className="h-3 w-3 text-info drop-shadow-[0_0_3px_rgba(14,165,233,0.5)]" />
                           )}
                           {isItemLocked && (
                             <LockClosedIcon className="h-3 w-3 text-text-secondary/60" />
                           )}
                           {item.count !== undefined && item.count > 0 && (
                             <span className={`text-[9px] px-1.5 py-0.5 rounded-md font-bold min-w-[18px] text-center transition-colors ${
                               isActive ? 'bg-primary text-white shadow-[0_0_8px_rgba(255,107,0,0.4)]' : 'bg-white/10 text-white/50 group-hover:bg-white/20 group-hover:text-white'
                             }`}>
                               {item.count}
                             </span>
                           )}
                           {(item as any).isBeta && (
                              <span className="text-[8px] font-black bg-primary/20 text-primary px-1.5 py-0.5 rounded-md border border-primary/30 uppercase tracking-widest leading-none shadow-[0_0_5px_rgba(255,107,0,0.2)]">
                                Beta
                              </span>
                           )}
                        </div>
                      </div>
                    )}
                    {isCollapsed && (
                      <div className="absolute left-14 bg-card border border-white/10 text-white text-xs px-2 py-1 rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible shadow-xl transition-all whitespace-nowrap pointer-events-none z-50">
                        {isItemLocked ? `🔒 ${item.name}` : `${item.name} ${item.count ? `(${item.count})` : ''}`}
                      </div>
                    )}
                  </Link>
                )
              })}
            </div>
          )}

          {/* Admin Link */}
          {user?.role === 'admin' && (
            <div className="pt-6">
               <Link
                to="/admin"
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 group relative ${
                  location.pathname === '/admin'
                    ? 'bg-purple-500/20 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.15)] border border-purple-500/30'
                    : 'text-text-secondary hover:bg-white/5 hover:text-white border border-transparent'
                }`}
              >
                <Crown className={`h-4 w-4 ${location.pathname === '/admin' ? 'text-purple-400 scale-110' : 'text-text-secondary group-hover:text-white group-hover:scale-110'}`} />
                {!isCollapsed && <span className="animate-in fade-in slide-in-from-left-1">Admin Panel</span>}
              </Link>
            </div>
          )}
        </nav>

        {/* Sidebar Footer - User Info */}
        <div className="p-3 border-t border-white/10 bg-black/20 backdrop-blur-md">
          <Link 
            to="/profile"
            className={`flex items-center gap-3 p-2 rounded-xl border border-white/5 bg-white/5 hover:border-primary/50 hover:bg-primary/10 transition-all duration-200 group relative ${
              isCollapsed ? 'justify-center' : ''
            }`}
          >
            <div className="w-8 h-8 rounded-full bg-app-bg border border-white/10 flex items-center justify-center text-text-secondary group-hover:text-primary transition-colors hover:shadow-[0_0_10px_rgba(255,107,0,0.3)]">
              <UserCircle className="h-5 w-5" />
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0 animate-in fade-in slide-in-from-left-1">
                <p className="text-xs font-bold text-white truncate">
                  {user ? `${user.firstName} ${user.lastName}` : 'Guest User'}
                </p>
                <p className="text-[10px] text-info font-medium truncate capitalize">
                  {(user as any)?.planType === 'intelligence' ? 'Intelligence' 
                    : (user as any)?.planType === 'starter' ? 'Starter' 
                    : 'Free'}
                </p>
              </div>
            )}
          </Link>

          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-2 mt-3 px-3 py-2 rounded-xl text-xs font-bold text-text-secondary hover:bg-error/10 hover:text-error transition-all duration-200 group ${
              isCollapsed ? 'justify-center' : ''
            }`}
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4 group-hover:scale-110 transition-transform" />
            {!isCollapsed && <span>Sign Out</span>}
          </button>
        </div>

        {/* Toggle Button */}
        <button
          onClick={toggleSidebar}
          className="absolute -right-3.5 top-[88px] w-7 h-7 rounded-full bg-card border border-white/10 shadow-[0_0_15px_rgba(0,0,0,0.6)] flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary text-text-secondary transition-all duration-200 z-50 focus:outline-none"
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {isCollapsed ? (
            <ChevronDoubleRightIcon className="h-3.5 w-3.5" />
          ) : (
            <ChevronDoubleLeftIcon className="h-3.5 w-3.5" />
          )}
        </button>
      </aside>

      {/* Mobile Menu Backdrop & Drawer */}
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 lg:hidden animate-in fade-in duration-300"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 w-80 glass z-[60] lg:hidden flex flex-col shadow-2xl animate-in slide-in-from-left duration-500 ease-out border-r border-white/10">
            <div className="h-16 flex items-center justify-between px-6 border-b border-white/10">
              <Link to="/dashboard" className="flex items-center" onClick={() => setIsMobileMenuOpen(false)}>
                <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-8 w-auto" style={{ filter: "drop-shadow(0px 0px 8px rgba(255,107,0,0.5))" }} />
              </Link>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 -mr-2 text-text-secondary hover:text-white">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
              <div className="text-[10px] font-black text-text-secondary uppercase tracking-widest mb-2 px-2">Main</div>
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all ${
                    location.pathname === item.href && !projectId ? 'bg-primary/20 text-primary border border-primary/30' : 'text-text-secondary hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </Link>
              ))}

              {projectId && (
                <>
                  <div className="flex items-center gap-2 mt-8 mb-2 px-2">
                     <span className="w-1.5 h-1.5 rounded-full bg-info shadow-[0_0_8px_theme(colors.info)] animate-pulse-subtle" />
                     <div className="text-[10px] font-black text-info uppercase tracking-widest">Agent Active</div>
                  </div>
                  {projectNavigation.map((item) => {
                    const isItemLocked = (item as any).isLocked
                    return (
                    <Link
                      key={item.name}
                      to={item.isDisabled || isItemLocked ? '#' : `/projects/${projectId}?tab=${item.tab}`}
                      onClick={(e) => {
                        if (item.isDisabled) {
                          e.preventDefault();
                        } else if (isItemLocked) {
                          e.preventDefault();
                          toast.error((item as any).lockTooltip || 'Complete prerequisites first', {
                            id: `locked-mobile-${item.tab}`,
                            duration: 3000,
                            icon: '🔒'
                          });
                        } else {
                          setIsMobileMenuOpen(false);
                        }
                      }}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all ${
                        activeTab === item.tab 
                         ? 'bg-primary/20 text-primary border border-primary/30' 
                         : item.isDisabled || isItemLocked
                           ? 'opacity-40 cursor-not-allowed text-text-secondary'
                           : 'text-text-secondary hover:bg-white/5 hover:text-white'
                      }`}
                      title={isItemLocked ? (item as any).lockTooltip : undefined}
                    >
                      {isItemLocked
                        ? <LockClosedIcon className="h-5 w-5" />
                        : <item.icon className="h-5 w-5" />}
                      {item.name}
                    </Link>
                  )})}

                </>
              )}
              
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-text-secondary hover:bg-purple-500/20 hover:text-purple-400"
                >
                  <Crown className="h-5 w-5" />
                  Admin Panel
                </Link>
              )}
            </nav>
            <div className="p-4 border-t border-white/10 bg-black/20">
              <Link 
                to="/profile" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-primary/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-app-bg border border-white/10 flex items-center justify-center text-text-secondary">
                  <UserCircle className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{user?.firstName} {user?.lastName}</p>
                  <p className="text-xs text-info capitalize">{user?.subscribed === 'S' ? 'Intelligence' : 'Starter'}</p>
                </div>
              </Link>
              <button
                onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 mt-4 px-3 py-2.5 rounded-xl text-sm font-bold text-error hover:bg-error/10 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                Sign Out
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main Content Area */}
      <div 
        className={`flex-1 flex flex-col min-h-screen transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          isCollapsed ? 'lg:pl-20' : 'lg:pl-64'
        }`}
      >
        {/* Header / Top Nav */}
        <header className="sticky top-0 z-30 h-16 glass border-b border-white/10 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-1.5 rounded-lg hover:bg-white/10 text-white"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <div className="flex items-center gap-2 text-[10px] font-bold text-text-secondary uppercase tracking-widest">
              <Link to="/dashboard" className="hover:text-primary transition-colors flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-info shadow-[0_0_5px_theme(colors.info)]" /> WORKSPACE
              </Link>
              <span className="opacity-30">/</span>
              <span className="text-white animate-in fade-in slide-in-from-left-2 duration-500">
                {activeProject ? activeProject.name : location.pathname.split('/').pop()?.replace(/-/g, ' ')}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
             {/* Live Agent / Upgrade */}
          </div>
        </header>

        {/* Dynamic Viewport */}
        <main className="flex-1 p-4 lg:p-6 animate-in fade-in slide-in-from-bottom-4 duration-500 relative z-10 w-full max-w-[1600px] mx-auto">
           {children}
        </main>
      </div>

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        message={limitMessage}
      />
    </div>
  )
}